import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ContentHeaderComponent } from '@/shared/components/content-header/content-header.component';
import { UserProfileService } from '@/pages/user-profile/user-profile.service';
import { HelpersService } from '@/shared/services/helpers.service';
import { AsyncState } from '@/shared/models/async-state.model';
import { RelationshipManagerDashboardService } from './relationship-manager-dashaboard.service';
import {
    RmClientSectorPreferenceDto,
    RmDashboardDealCard,
    RmOpenActionsResponseDto,
} from './data-access/relationship-manager-dashboard.model';
import { RmStatsWidget } from './components/rm-statswidget/rm-statswidget';
import { RmOpenActionsComponent } from './components/rm-open-actions/rm-open-actions.component';
import { RmAiSummaryComponent } from './components/rm-ai-summary/rm-ai-summary.component';
import { AI_FEATURE_ENABLED } from '@/shared/utilities/feature-flags';
import { RmDealCardsComponent } from './components/rm-deal-cards/rm-deal-cards.component';
import { RmLiveDealsComponent } from './components/rm-live-deals/rm-live-deals.component';
import { RmSectorPreferencesComponent } from './components/rm-sector-preferences/rm-sector-preferences.component';

@Component({
    selector: 'app-relationship-manager-dashboard',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        ContentHeaderComponent,
        RmStatsWidget,
        RmOpenActionsComponent,
        RmAiSummaryComponent,
        RmLiveDealsComponent,
        RmDealCardsComponent,
        RmSectorPreferencesComponent,
    ],
    templateUrl: './relationship-manager-dashboard.component.html',
    styles: ``,
})
export class RelationshipManagerDashboardComponent implements OnInit {
    protected readonly aiFeatureEnabled = AI_FEATURE_ENABLED;
    private readonly _translateService = inject(TranslateService);
    private readonly _rmDashboardService = inject(RelationshipManagerDashboardService);
    private readonly _userProfileService = inject(UserProfileService);
    private readonly _helpersService = inject(HelpersService);

    public readonly openActionsState = signal<AsyncState<RmOpenActionsResponseDto>>({
        data: null,
        loading: false,
        error: null,
    });

    public readonly closingSoonState = signal<AsyncState<{ items: RmDashboardDealCard[]; totalCount: number }>>({
        data: null,
        loading: false,
        error: null,
    });

    public readonly sectorPreferencesState = signal<AsyncState<RmClientSectorPreferenceDto[]>>({
        data: null,
        loading: false,
        error: null,
    });

    public ngOnInit(): void {
        this._userProfileService.getUserProfile();
        this.loadOpenActions();
        this.loadClosingSoon();
        this.loadSectorPreferences();
    }

    public get dashboardHeaderTitle(): string {
        const profile = this._userProfileService.userProfileData();
        const baseTitle = this._translateService.instant('RM.DASHBOARD.TITLE') as string;
        const fullName = `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim() || profile?.name || '';
        const roleLabel = profile?.roleLabel ? (this._translateService.instant(profile.roleLabel) as string) : '';
        return [baseTitle, fullName, roleLabel].filter(Boolean).join(' - ');
    }

    private loadOpenActions(): void {
        this.openActionsState.update((state) => ({ ...state, loading: true, error: null }));
        this._rmDashboardService
            .getRelationshipManagerOpenActions(5)
            .pipe(finalize(() => this.openActionsState.update((state) => ({ ...state, loading: false }))))
            .subscribe({
                next: (data) => this.openActionsState.set({ data, loading: false, error: null }),
                error: (err: unknown) => {
                    this.openActionsState.set({ data: { items: [], totalCount: 0 }, loading: false, error: err });
                    this._helpersService.showErrorMessage(err as never);
                },
            });
    }

    private loadClosingSoon(): void {
        this.closingSoonState.update((state) => ({ ...state, loading: true, error: null }));
        this._rmDashboardService
            .getRelationshipManagerClosingSoon(14, 2)
            .pipe(finalize(() => this.closingSoonState.update((state) => ({ ...state, loading: false }))))
            .subscribe({
                next: (data) =>
                    this.closingSoonState.set({
                        data: { items: data.items.slice(0, 2), totalCount: data.totalCount },
                        loading: false,
                        error: null,
                    }),
                error: (err: unknown) => {
                    this.closingSoonState.set({ data: { items: [], totalCount: 0 }, loading: false, error: err });
                    this._helpersService.showErrorMessage(err as never);
                },
            });
    }

    private loadSectorPreferences(): void {
        this.sectorPreferencesState.update((state) => ({ ...state, loading: true, error: null }));
        this._rmDashboardService
            .getRelationshipManagerClientSectorPreferences()
            .pipe(finalize(() => this.sectorPreferencesState.update((state) => ({ ...state, loading: false }))))
            .subscribe({
                next: (data) => this.sectorPreferencesState.set({ data, loading: false, error: null }),
                error: (err: unknown) => {
                    this.sectorPreferencesState.set({ data: [], loading: false, error: err });
                    this._helpersService.showErrorMessage(err as never);
                },
            });
    }
}
